import React, { useContext, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'
import UserContext from 'containers/user/userContext'
import InputField from 'components/InputField'
import ScreenLoading from 'components/ScreenLoading'
import Divider from 'components/Divider'
import api from 'containers/api'

const ProfileTab = () => {
  const user = useContext(UserContext)
  const token = useSelector(selectToken)
  const [isEditing, setIsEditing] = useState(false)
  const [firstName, setFirstName] = useState(user?.profile?.first_name || '')
  const [lastName, setLastName] = useState(user?.profile?.last_name || '')
  const [pendingRequest, setPendingRequest] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (user?.profile) {
      setFirstName(user.profile.first_name || '')
      setLastName(user.profile.last_name || '')
    }
  }, [user])

  const handleEdit = () => {
    setSaveSuccess('')
    setError('')
    setIsEditing(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isEditing) {
      return
    }

    setPendingRequest(true)
    setSaveSuccess('')
    setError('')

    try {
      await api.updateUserProfile({ firstName, lastName, token })
      setSaveSuccess('Profile updated successfully')
      setIsEditing(false)
    } catch (err) {
      console.error('Error updating profile:', err)
      setError('Failed to update profile. Please try again.')
    } finally {
      setPendingRequest(false)
    }
  }

  const handleCancel = () => {
    setFirstName(user?.profile?.first_name || '')
    setLastName(user?.profile?.last_name || '')
    setIsEditing(false)
    setError('')
    setSaveSuccess('')
  }

  return (
    <div style={{ marginTop: '1rem', maxWidth: '600px', padding: '0 1rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '0.5rem', wordBreak: 'break-word' }}>Email</h3>
        <p style={{ color: '#6c757d', fontSize: '0.95rem', wordBreak: 'break-all' }}>{user?.email}</p>
        <small style={{ color: '#999', fontSize: '0.85rem' }}>
          Email cannot be changed
        </small>
      </div>

      <Divider spaceY={20} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
        {isEditing ? (
          <>
            <InputField
              label="First Name"
              type="text"
              value={firstName}
              setCurrentValue={setFirstName}
              style={{ margin: '0' }}
            />
            <InputField
              label="Last Name"
              type="text"
              value={lastName}
              setCurrentValue={setLastName}
              style={{ margin: '0' }}
            />
          </>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: '#495057' }}>
                First Name
              </label>
              <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px', color: '#6c757d' }}>
                {firstName || 'Not set'}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: '#495057' }}>
                Last Name
              </label>
              <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px', color: '#6c757d' }}>
                {lastName || 'Not set'}
              </div>
            </div>
          </>
        )}
      </div>

      {error && <p className="form-error">{error}</p>}
      {saveSuccess && <p className="form-success">{saveSuccess}</p>}

      <div style={{ marginTop: '1.8rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {!isEditing ? (
          <button
            type="button"
            className="submit-button"
            onClick={handleEdit}
            style={{ minWidth: '120px' }}
          >
            Edit Profile
          </button>
        ) : (
          <>
            <button
              type="button"
              className="submit-button"
              onClick={() => handleSave({ preventDefault: () => {} } as React.FormEvent)}
              disabled={pendingRequest}
              style={{ minWidth: '120px' }}
            >
              {pendingRequest ? <ScreenLoading spinnerColor="white" /> : 'Save'}
            </button>
            <button
              type="button"
              className="submit-button"
              style={{ backgroundColor: '#6c757d', minWidth: '120px' }}
              onClick={handleCancel}
              disabled={pendingRequest}
            >
              Cancel
            </button>
          </>
        )}
      </div>

      <Divider spaceY={30} />

      <div>
        <h3 style={{ marginBottom: '0.5rem' }}>Profile Picture</h3>
        <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Profile picture upload coming soon
        </p>
        <div
          style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            backgroundColor: '#e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            color: '#6c757d',
          }}
        >
          {user?.profile?.first_name?.[0]?.toUpperCase() ||
           user?.email?.[0]?.toUpperCase() ||
           '?'}
        </div>
      </div>
    </div>
  )
}

export default ProfileTab
